import React from 'react';
import styled from 'styled-components';
import { StepResult, SyncStatusResponse } from '../WbStores/wb-stores-api';

// --- Step config ---

export interface StepConfig {
  label: string;
  syncKey: string;
  resultName: string;
}

export const WB_STEPS: StepConfig[] = [
  { label: 'Товары',      syncKey: 'products',      resultName: 'products' },
  { label: 'Продажи',     syncKey: 'sales',          resultName: 'realization' },
  { label: 'Дн. продажи', syncKey: 'daily-sales',    resultName: 'daily_sales' },
  { label: 'Заказы',      syncKey: 'orders',         resultName: 'orders' },
  { label: 'Хранение',    syncKey: 'storage',        resultName: 'storage' },
  { label: 'Приёмка',     syncKey: 'acceptance',     resultName: 'acceptance' },
  { label: 'Реклама',     syncKey: 'ad-costs',       resultName: 'ad_costs' },
  { label: 'Карточки',    syncKey: 'product-cards',  resultName: 'product_cards' },
  { label: 'Тарифы',      syncKey: 'tariffs',        resultName: 'tariffs' },
  { label: 'Остатки',     syncKey: 'stocks',         resultName: 'stocks' },
  { label: 'Ключи',       syncKey: 'keywords',       resultName: 'keywords' },
  { label: 'Воронка',     syncKey: 'funnel',         resultName: 'funnel' },
];

export const OZON_STEPS: StepConfig[] = [
  { label: 'Товары',    syncKey: 'products',    resultName: 'products' },
  { label: 'Остатки',   syncKey: 'stocks',      resultName: 'stocks' },
  { label: 'Финансы',   syncKey: 'finances',    resultName: 'finances' },
  { label: 'Аналитика', syncKey: 'analytics',   resultName: 'analytics' },
  { label: 'Реклама',   syncKey: 'performance', resultName: 'performance' },
  { label: 'Прогнозы',  syncKey: 'forecasts',   resultName: 'forecasts' },
];

// --- Styled components ---

const Panel = styled.div`
  padding: 12px 16px;
  background: #fafafa;
  border-top: 1px solid #e8e8e8;
  border-bottom: 2px solid #d0e4ff;
`;

const PanelLabel = styled.div`
  font-size: 11px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
`;

type TileStatus = 'ok' | 'error' | 'running' | 'skipped' | 'never';

const Tile = styled.div<{ $status: TileStatus }>`
  background: white;
  border-radius: 5px;
  padding: 7px 8px;
  cursor: ${p => (p.$status === 'running' ? 'not-allowed' : 'pointer')};
  opacity: ${p => (p.$status === 'never' ? 0.65 : 1)};
  border: 1px solid ${p => {
    if (p.$status === 'ok') return '#c8e6c9';
    if (p.$status === 'error') return '#ffcdd2';
    if (p.$status === 'running') return '#fff9c4';
    return '#e0e0e0';
  }};
  border-left: ${p => (p.$status === 'error' ? '3px solid #f44336' : 'none')};

  &:hover {
    background: ${p => {
      if (p.$status === 'ok') return '#f1f8e9';
      if (p.$status === 'error') return '#fff5f5';
      if (p.$status === 'never') return '#f5f5f5';
      return 'white';
    }};
  }
`;

const TileTitle = styled.div<{ $running?: boolean }>`
  font-weight: 600;
  font-size: 11px;
  color: ${p => (p.$running ? '#aaa' : '#333')};
`;

const TileStatusText = styled.div<{ $status: TileStatus }>`
  font-size: 10px;
  margin-top: 2px;
  color: ${p => {
    if (p.$status === 'ok') return '#4caf50';
    if (p.$status === 'error') return '#f44336';
    if (p.$status === 'running') return '#f57f17';
    return '#9e9e9e';
  }};
`;

// --- Helpers ---

// formatDuration форматирует миллисекунды в читаемую строку (например "1м 23с", "45с", "320мс")
function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}мс`;
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}с`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return sec > 0 ? `${min}м ${sec}с` : `${min}м`;
}

function getTileState(
  step: StepConfig,
  stepResultMap: Record<string, StepResult>,
  isJobRunning: boolean
): { status: TileStatus; label: string } {
  if (step.resultName === '') {
    return { status: 'never', label: '— Никогда' };
  }

  const result = stepResultMap[step.resultName];
  if (!result) {
    if (isJobRunning) {
      return { status: 'running', label: '⟳ Идёт...' };
    }
    return { status: 'never', label: '— Никогда' };
  }

  if (result.status === 'ok') {
    const duration = result.duration_ms ? formatDuration(result.duration_ms) : '';
    return { status: 'ok', label: duration ? `✓ ${duration}` : '✓' };
  }
  if (result.status === 'skipped') {
    const duration = result.duration_ms ? formatDuration(result.duration_ms) : '';
    return { status: 'skipped', label: duration ? `○ ${duration}` : '○ Пропущено' };
  }
  if (result.status.startsWith('error:')) {
    return { status: 'error', label: `✕ ${result.status.replace('error: ', '')}` };
  }
  return { status: 'never', label: '— Никогда' };
}

// --- Props ---

interface SyncStatusPanelProps {
  status: SyncStatusResponse | null;
  steps: StepConfig[];
  onTriggerStep: (syncKey: string) => void;
  finishedAt?: string | null;
  runningSyncs?: Set<string>;
}

export const SyncStatusPanel: React.FC<SyncStatusPanelProps> = ({
  status,
  steps,
  onTriggerStep,
  finishedAt,
  runningSyncs,
}) => {
  const isJobRunning = status?.job_status === 'running';

  const stepResultMap: Record<string, StepResult> = {};
  if (status?.steps) {
    for (const s of status.steps) {
      stepResultMap[s.name] = s;
    }
  }

  return (
    <Panel>
      <PanelLabel>Синхронизации — нажми тайл для запуска</PanelLabel>
      <TileGrid>
        {steps.map(step => {
          const { status: tileStatus, label } = getTileState(step, stepResultMap, isJobRunning);
          const isSyncing = runningSyncs?.has(step.syncKey) ?? false;
          const isClickable = tileStatus !== 'running' && !isSyncing;

          return (
            <Tile
              key={step.syncKey}
              $status={isSyncing ? 'running' : tileStatus}
              onClick={() => isClickable && onTriggerStep(step.syncKey)}
              title={step.label}
            >
              <TileTitle $running={tileStatus === 'running' || isSyncing}>
                {(tileStatus === 'running' || isSyncing) ? `⟳ ${step.label}` : `▶ ${step.label}`}
              </TileTitle>
              <TileStatusText $status={isSyncing ? 'running' : tileStatus}>
                {isSyncing ? '⟳ Идёт...' : label}
              </TileStatusText>
            </Tile>
          );
        })}
      </TileGrid>
    </Panel>
  );
};
